<template>
  <span class="reveal-field" :class="variant">
    <input
      v-model="model"
      v-bind="$attrs"
      :type="shown ? 'text' : 'password'"
    />
    <button
      type="button"
      class="reveal"
      :aria-pressed="shown"
      :aria-label="shown ? t('auth.hidePassword') : t('auth.showPassword')"
      @mousedown.prevent
      @click="shown = !shown"
    >
      <svg v-if="shown" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 6.5c3.79 0 7.17 2.13 8.82 5.5-1.65 3.37-5.03 5.5-8.82 5.5S4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5zm0 1.8A3.7 3.7 0 1 0 12 15.7 3.7 3.7 0 0 0 12 8.3z"
        />
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          d="M4 5.5 19.5 19"
        />
      </svg>
      <svg v-else viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
        />
      </svg>
    </button>
  </span>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from '../i18n/t'

defineOptions({ inheritAttrs: false })

withDefaults(
  defineProps<{
    variant?: 'auth' | 'md' | 'settings'
  }>(),
  { variant: 'auth' },
)

const model = defineModel<string>({ required: true })
const shown = ref(false)
const { t } = useI18n()
</script>

<style scoped>
.reveal-field {
  position: relative;
  display: block;
  min-width: 0;
}

.reveal-field input {
  width: 100%;
  box-sizing: border-box;
  padding-inline-end: 44px;
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
  font-weight: 400;
}

.reveal-field.auth input {
  min-height: 44px;
  padding: 10px 44px 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
}

.reveal-field.auth input:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.reveal-field.md input {
  padding: 10px 44px 10px 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 8px;
  background: var(--md-sys-color-surface-container);
}

.reveal-field.md input:focus {
  outline: none;
  border-color: var(--md-sys-color-primary);
}

.reveal-field.settings {
  flex: 1 1 200px;
}

.reveal-field.settings input {
  min-height: 44px;
  padding: 10px 44px 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
}

.reveal-field.settings input:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.reveal {
  position: absolute;
  inset-inline-end: 2px;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 20px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  cursor: pointer;
}

.reveal:hover {
  background: var(--md-state-layer);
}

.reveal:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}
</style>
